/**
 * Collybus Webhook Schemas
 *
 * Raw webhook payloads from Collybus provider.
 * Used by: payment-service/src/webhooks/collybus.ts
 *
 * Endpoint:
 *   POST /webhook/collybus
 *
 * Security:
 *   - X-Signature header: HMAC-SHA256 of canonicalized JSON body
 *   - Secret key: COLLYBUS_API_KEY
 */
import { z } from 'zod';
// =============================================================================
// COLLYBUS WEBHOOK BODY
// Source: payment-service/src/webhooks/collybus.ts:36-44
// =============================================================================
/**
 * Collybus webhook payload
 *
 * Example:
 * {
 *   id: "19c278c1-79a4-4836-908b-84a36e43dde8",
 *   internal_id: "12345",
 *   payment_method: "card",
 *   currency: "RUB",
 *   initial_amount: "5000",
 *   final_amount: "5000",
 *   status: "success" | "cancelled" | "expired"
 * }
 */
export const collybusWebhookBodySchema = z.object({
    /** Collybus transaction UUID */
    id: z.string().optional(),
    /** Our internal reference ID */
    internal_id: z.string().optional(),
    /** Payment method: "card" */
    payment_method: z.string().optional(),
    /** Currency code: "RUB" */
    currency: z.string().optional(),
    /** Initial requested amount (string) */
    initial_amount: z.string().optional(),
    /** Final confirmed amount (string) */
    final_amount: z.string().optional(),
    /** Raw status: success, cancelled, expired, failed, processing */
    status: z.string().optional(),
}).passthrough();
// =============================================================================
// VALIDATION HELPERS
// =============================================================================
/**
 * Checks if required fields are present
 */
export function hasCollybusRequiredFields(body) {
    return !!(body.id && body.status);
}
/**
 * Parses amount from string to number
 */
export function parseCollybusAmount(body) {
    return body.final_amount ? parseFloat(body.final_amount) : undefined;
}
// =============================================================================
// STATUS MAPPING
// =============================================================================
/**
 * Maps Collybus raw status to normalized WebhookStatus
 * - success/completed → completed
 * - cancelled/canceled/expired → cancelled
 * - failed → failed
 * - processing → pending
 */
export function mapCollybusStatus(status) {
    switch (status?.toLowerCase()) {
        case 'success':
        case 'completed':
            return 'completed';
        case 'cancelled':
        case 'canceled':
            return 'cancelled';
        case 'expired':
            return 'cancelled';
        case 'failed':
            return 'failed';
        case 'processing':
            return 'pending';
        default:
            return 'pending';
    }
}
//# sourceMappingURL=collybus.js.map